import { BACKUP_FORMAT, BACKUP_FORMAT_VERSION, verifyBackup, type BackupEnvelope } from './exportBackup';
import { toArrayBuffer, unzipEntries } from './zip';
import type { MediaManifestEntry } from './exportFullBackup';

/**
 * Reading and validating a backup file, before anything touches Dexie.
 * Master Phase 9 — Phase 0 §11.5 step 1: "reject unknown format, a newer
 * formatVersion, or a checksum mismatch — before touching anything."
 */

export class RestoreValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RestoreValidationError';
  }
}

export interface ParsedBackup {
  envelope: BackupEnvelope;
  /** Present only for a `.zip` import that actually carried media. */
  media: { manifest: MediaManifestEntry[]; files: Map<string, Blob> } | null;
}

const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];

/** Content, not the filename — a renamed file must not silently misparse. */
function looksLikeZip(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && ZIP_MAGIC.every((byte, index) => bytes[index] === byte);
}

/** Runtime shape check — a parsed JSON value is `unknown` until proven otherwise. */
function assertEnvelopeShape(value: unknown): asserts value is BackupEnvelope {
  if (value === null || typeof value !== 'object') {
    throw new RestoreValidationError('This file does not look like a LiftX backup.');
  }
  const candidate = value as Record<string, unknown>;
  if (candidate.format !== BACKUP_FORMAT) {
    throw new RestoreValidationError('This file is not a LiftX backup.');
  }
  if (typeof candidate.formatVersion !== 'number' || candidate.formatVersion > BACKUP_FORMAT_VERSION) {
    throw new RestoreValidationError(
      'This backup was made with a newer version of LiftX than this device is running — update the app first.',
    );
  }
  if (typeof candidate.payload !== 'object' || candidate.payload === null) {
    throw new RestoreValidationError('This backup is missing its data payload.');
  }
  if (typeof candidate.checksum !== 'string') {
    throw new RestoreValidationError('This backup is missing its checksum.');
  }
}

async function parseZip(bytes: Uint8Array): Promise<ParsedBackup> {
  const entries = await unzipEntries(bytes);

  const backupJson = entries['backup.json'];
  if (backupJson === undefined) {
    throw new RestoreValidationError('This zip does not contain a backup.json — not a LiftX backup.');
  }

  let envelopeValue: unknown;
  try {
    envelopeValue = JSON.parse(new TextDecoder().decode(backupJson));
  } catch {
    throw new RestoreValidationError('This backup’s data could not be read — the file may be corrupted.');
  }
  assertEnvelopeShape(envelopeValue);

  let media: ParsedBackup['media'] = null;
  const manifestBytes = entries['media/manifest.json'];
  if (manifestBytes !== undefined) {
    const manifest = JSON.parse(new TextDecoder().decode(manifestBytes)) as MediaManifestEntry[];
    const files = new Map<string, Blob>();
    for (const entry of manifest) {
      const fileBytes = entries[`media/${entry.fileName}`];
      if (fileBytes !== undefined) {
        files.set(entry.fileName, new Blob([toArrayBuffer(fileBytes)], { type: entry.mimeType }));
      }
      if (entry.thumbnailFileName !== null) {
        const thumbnailBytes = entries[`media/${entry.thumbnailFileName}`];
        if (thumbnailBytes !== undefined) {
          files.set(entry.thumbnailFileName, new Blob([toArrayBuffer(thumbnailBytes)]));
        }
      }
    }
    media = { manifest, files };
  }

  return { envelope: envelopeValue, media };
}

function parseJson(bytes: Uint8Array): ParsedBackup {
  let envelopeValue: unknown;
  try {
    envelopeValue = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new RestoreValidationError('This file is not valid JSON and not a zip — it does not look like a LiftX backup.');
  }
  assertEnvelopeShape(envelopeValue);
  return { envelope: envelopeValue, media: null };
}

/**
 * Read and validate a backup file end to end. Throws `RestoreValidationError`
 * on anything wrong with the file itself; never touches Dexie.
 */
export async function parseBackupFile(file: File): Promise<ParsedBackup> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const parsed = looksLikeZip(bytes) ? await parseZip(bytes) : parseJson(bytes);

  // `schemaVersion` is informational only, never a validation gate here — a
  // backup from an older-but-compatible schema (v2→v3 changed no row
  // shapes) is the normal restore case, not a rejected one.
  if (!(await verifyBackup(parsed.envelope))) {
    throw new RestoreValidationError('This backup failed its checksum check — the file may be corrupted or was edited.');
  }

  return parsed;
}
